import styled from '@emotion/styled';
import { ButtonBase, Popover } from '@material-ui/core';
import { useState } from 'react';
import { useUnsafeTranslation } from '../../../../../../i18next';
import { tokens } from '../../../../components/Builders/newux/kit';
import { useActorPickerColors } from './actorPickerColors';

const SWATCH_EDGE = 'inset 0 0 0 1px rgba(8, 18, 26, 0.08)';

const SwatchButton = styled(ButtonBase)((p) => {
  const fieldHeight = p.theme.spacing(5);
  return {
    '&&': {
      width: fieldHeight,
      height: fieldHeight,
      borderRadius: p.theme.custom.border.radius(),
      border: p.theme.custom.border.standard,
      backgroundColor: 'white'
    },
    '&&:hover': {
      borderColor: p.theme.custom.colors.gray.w400,
      boxShadow: '0 0 0 4px rgba(119,128,136,.1)'
    },
    '&&.Mui-disabled': { backgroundColor: p.theme.custom.colors.gray.w100 }
  };
});

const Swatch = styled('span')({
  width: 24,
  height: 24,
  borderRadius: 4,
  boxShadow: SWATCH_EDGE
});

const PalettePopover = styled(Popover)((p) => ({
  '& .MuiPopover-paper': {
    padding: 14,
    borderRadius: 6,
    boxShadow: `inset 0 0 0 1px ${p.theme.custom.colors.gray.w200}, ${tokens.shadow.dialog}`
  }
}));

const PaletteGrid = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(5, 34px)',
  gridTemplateRows: 'repeat(3, 34px)',
  gap: 10
});

const PaletteOption = styled(ButtonBase)<{ selected: boolean }>((p) => ({
  '&&': {
    width: 34,
    height: 34,
    borderRadius: 4,
    boxShadow: p.selected
      ? `0 0 0 2px white, 0 0 0 3.5px ${p.theme.custom.colors.blue.w500}`
      : undefined
  },
  '&&:hover': {
    boxShadow: `0 0 0 2px white, 0 0 0 3.5px ${
      p.selected ? p.theme.custom.colors.blue.w500 : tokens.color.textDisabled
    }`
  }
}));

// The actor's colour: a swatch that opens the palette grid below it.
export const ActorColorField = ({
  value,
  onChange,
  disabled
}: {
  value: string;
  onChange: (nextColor: string) => void;
  disabled?: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  const colors = useActorPickerColors();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  return (
    <>
      <SwatchButton
        disabled={disabled}
        aria-label={t('Color')}
        onClick={(ev) => setAnchorEl(ev.currentTarget)}
      >
        <Swatch style={{ background: value }} />
      </SwatchButton>
      <PalettePopover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: -6, horizontal: 'left' }}
      >
        <PaletteGrid>
          {colors.map((c) => (
            <PaletteOption
              key={c}
              selected={c.toLowerCase() === value.toLowerCase()}
              style={{ background: c }}
              onClick={() => {
                onChange(c);
                setAnchorEl(null);
              }}
            />
          ))}
        </PaletteGrid>
      </PalettePopover>
    </>
  );
};
